// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.0.6 — use case → primary persona.
 *
 * Replaces `UC_TO_PRIMARY_PERSONA`, a hand-maintained map inside
 * `PersonaOnboardingPage.tsx` that covered `UC-VN-01`…`27`. The corpus now has 48
 * authored use cases, so 21 of them — every EU, UK and global one, and every
 * VN use case added from `UC-VN-28` on — resolved to nothing and the
 * `?useCase=` deep link silently no-opped.
 *
 * ## The source is the use-case documents, not the matrix JSON
 *
 * The obvious source was `docs/user-validation/matrices/framework-persona-map.json`,
 * but at Sprint 62.0.6 it declared **46** use cases where the corpus had **47**:
 * `UC-VN-27` (DPIA Recurring Review) had an authored document and no matrix
 * entry. The 2026-08-12 governance audit repaired that mapping and added the
 * inverse corpus-to-map test; this registry remains document-derived so runtime
 * onboarding is not coupled to the framework matrix's different responsibility.
 *
 * These 48 entries come from the `**ID:**` and `## Primary Persona` sections of
 * `docs/user-validation/use-cases/*.md`. Cross-checked before adoption: all 48
 * documents declare a primary persona, and the 27 the old map covered agree
 * with their documents in **every** case, so no existing deep link changes
 * where it lands. `persona-registry-drift.test.ts` re-runs that comparison.
 */
export const USE_CASE_PRIMARY_PERSONA = {
    'UC-EU-01': 'P-EU-01',
    'UC-EU-02': 'P-EU-01',
    'UC-EU-03': 'P-EU-02',
    'UC-EU-04': 'P-EU-03',
    'UC-EU-05': 'P-EU-04',
    'UC-GL-01': 'P-GL-01',
    'UC-GL-02': 'P-GL-02',
    'UC-UK-01': 'P-UK-01',
    'UC-UK-02': 'P-UK-02',
    'UC-VN-01': 'P-VN-01',
    'UC-VN-02': 'P-VN-03',
    'UC-VN-03': 'P-VN-04',
    'UC-VN-04': 'P-VN-05',
    'UC-VN-05': 'P-VN-06',
    'UC-VN-06': 'P-VN-07',
    'UC-VN-07': 'P-VN-08',
    'UC-VN-08': 'P-VN-09',
    'UC-VN-09': 'P-VN-10',
    'UC-VN-10': 'P-VN-05',
    'UC-VN-11': 'P-VN-01',
    'UC-VN-12': 'P-VN-01',
    'UC-VN-13': 'P-VN-01',
    'UC-VN-14': 'P-VN-08',
    'UC-VN-15': 'P-VN-01',
    'UC-VN-16': 'P-VN-10',
    'UC-VN-17': 'P-VN-01',
    'UC-VN-18': 'P-VN-01',
    'UC-VN-19': 'P-VN-05',
    'UC-VN-20': 'P-VN-01',
    'UC-VN-21': 'P-VN-14',
    'UC-VN-22': 'P-VN-12',
    'UC-VN-23': 'P-VN-13',
    'UC-VN-24': 'P-VN-14',
    'UC-VN-25': 'P-VN-12',
    'UC-VN-26': 'P-VN-11',
    'UC-VN-27': 'P-VN-01',
    'UC-VN-28': 'P-VN-17',
    'UC-VN-29': 'P-VN-18',
    'UC-VN-30': 'P-VN-19',
    'UC-VN-31': 'P-VN-20',
    'UC-VN-32': 'P-VN-21',
    'UC-VN-33': 'P-VN-22',
    'UC-VN-34': 'P-VN-23',
    'UC-VN-35': 'P-VN-24',
    'UC-VN-36': 'P-VN-25',
    'UC-VN-37': 'P-VN-26',
    'UC-VN-38': 'P-VN-27',
    // Sprint 62.7.0 — seller / transaction / e-invoice reconciliation and
    // tax-authority correspondence. Primary is P-VN-28, not P-VN-03: the
    // e-commerce compliance manager owns registration dossiers, and none of that
    // persona's validation questions is answered by this journey.
    'UC-VN-39': 'P-VN-28',
    // Sprint 62.15.0 — the decree wave. UC-VN-40's primary is P-VN-21 rather
    // than a new persona: the National Single Window succession is the same
    // customs lead's job under a dated pair of instruments, which is what
    // re-anchoring P-VN-21 on both codes says. UC-VN-41's is P-VN-10, the audit
    // and inspection lead, because a penalty inspection is an inspection before
    // it is a penalty.
    'UC-VN-40': 'P-VN-21',
    'UC-VN-41': 'P-VN-10',
    'UC-VN-42': 'P-VN-29',
    'UC-VN-43': 'P-VN-30',
    // Sprint 62.16.0 — the September sector instruments. UC-VN-45's primary is
    // P-VN-13 rather than a new persona: the digital student record is the same
    // education compliance lead's job beneath Decree 88/2026, which is what
    // re-anchoring P-VN-13 on the circular says.
    'UC-VN-44': 'P-VN-31',
    'UC-VN-45': 'P-VN-13',
    'UC-VN-46': 'P-VN-32',
    // 2026-09-26 — created by the Circular 41/2024/TT-NHNN intake (Framework
    // Intake Trigger): payment-system supervisory reporting and inspection.
    'UC-VN-47': 'P-VN-23',
    // Law 23/2026/QH16 intake — a licensed crypto-asset provider becomes an
    // anti-money-laundering reporting entity on 1 December 2026. Both belong to
    // the provider's compliance lead, who owns the programme the Law creates.
    'UC-VN-48': 'P-VN-32',
    'UC-VN-49': 'P-VN-32',
    // 2026-10-04 — created by the Circular 154/2026/TT-BCA intake (Framework
    // Intake Trigger): specially controlled chemicals on the Ministry of Public
    // Security's identification and traceability system. Both belong to the EHS &
    // chemical steward who owns the chemical evidence set: UC-VN-50 the routine
    // half (scope, VNeID access, System-issued codes, events, control form,
    // termination), UC-VN-51 the 24-hour declaration and the misuse notice.
    'UC-VN-50': 'P-VN-19',
    'UC-VN-51': 'P-VN-19',
    // 2026-10-04 — Decree 372/2026/NĐ-CP intake. Product identification,
    // significant events, certificate synchronisation and supplier correction
    // belong to the quality and traceability lead who owns the product master;
    // P-VN-33 and P-VN-34 support it. Written as UC-VN-50 and renumbered on
    // merging main, whose Circular 154 intake took UC-VN-50 and UC-VN-51.
    'UC-VN-52': 'P-VN-17',
    // 2026-10-10 — created by the food-sector intake (Circular 63/2026/TT-BCT and
    // Circular 11/2026/TT-BCT, suspended): onboarding and database records,
    // certificates, batch traceability, inspection and testing, warnings and
    // recall, violations, complaints, API synchronisation, access and correction.
    // Food manufacturers and processors own the producer's workflows; the trader
    // owns complaints received at the point of sale; the integration custodian
    // owns the route and its receipts; the records administrator owns access and
    // correction.
    'UC-VN-65': 'P-VN-43',
    'UC-VN-66': 'P-VN-43',
    'UC-VN-67': 'P-VN-43',
    'UC-VN-68': 'P-VN-43',
    'UC-VN-69': 'P-VN-43',
    'UC-VN-70': 'P-VN-43',
    'UC-VN-71': 'P-VN-44',
    'UC-VN-72': 'P-VN-34',
    'UC-VN-73': 'P-VN-45',
    'UC-VN-74': 'P-VN-45',
    // 2026-10-10 — Decree 382/2026/NĐ-CP intake's persona and use-case gate. The
    // applicant company owns each step of the controlled-test case: admission,
    // solution adjustment, extension, stop and completion. P-VN-36 and P-VN-37
    // support them and are recorded in the gate (docs/user-validation).
    'UC-VN-53': 'P-VN-35',
    'UC-VN-54': 'P-VN-35',
    'UC-VN-55': 'P-VN-35',
    'UC-VN-56': 'P-VN-35',
    'UC-VN-57': 'P-VN-35',
    // 2026-10-10 — created by the Circular 53/2026/TT-NHNN banking AI-governance
    // gate (docs/user-validation/circular-53-banking-ai-gate.md, section 5.2). Each
    // belongs to the persona that holds its record: UC-VN-58 the first-line AI
    // system owner, UC-VN-59 the security-testing lead, UC-VN-60 the payments
    // evidence lead for authority limits, UC-VN-61 the channel supervisor, UC-VN-62
    // the complaints lead, UC-VN-63 the vendor-governance lead, and UC-VN-64 the
    // second-line compliance officer who keeps the bank-wide register.
    'UC-VN-58': 'P-VN-38',
    'UC-VN-59': 'P-VN-06',
    'UC-VN-60': 'P-VN-23',
    'UC-VN-61': 'P-VN-41',
    'UC-VN-62': 'P-VN-40',
    'UC-VN-63': 'P-VN-08',
    'UC-VN-64': 'P-VN-42',
    'UC-VN-75': 'P-VN-37',
    // 2026-10-22 — created by the October 2026 intakes' persona and use-case gate
    // (docs/user-validation/october-2026-intakes-persona-usecase-gate.md, section
    // 8.2). The people's credit fund's lead owns its requirement, information and
    // enquiry records; the data-governance lead owns the recipient's register, the
    // exploitation route and grant, and end-of-purpose disposition; the data
    // innovation lead owns controlled testing and incubator participation; the
    // cybersecurity lead owns the incident notice and the voluntary self-assessment;
    // the vendor-governance lead owns foreign-cooperation risk and supplier
    // evidence; and the State body's assurance lead owns the pilot period and its
    // statistics. UC-VN-76 is reserved by the Sprint 63.26.0 close-out report.
    'UC-VN-77': 'P-VN-47',
    'UC-VN-78': 'P-VN-47',
    'UC-VN-79': 'P-VN-47',
    'UC-VN-80': 'P-VN-05',
    'UC-VN-81': 'P-VN-05',
    'UC-VN-82': 'P-VN-49',
    'UC-VN-83': 'P-VN-06',
    'UC-VN-84': 'P-VN-05',
    'UC-VN-85': 'P-VN-08',
    'UC-VN-86': 'P-VN-49',
    'UC-VN-87': 'P-VN-48',
    'UC-VN-88': 'P-VN-06',
    'UC-VN-89': 'P-VN-48',
    'UC-VN-90': 'P-VN-08',
    // 2026-10-22 — the same gate's owner-decision section (§11), after owner
    // decision #661 packaged Circular 18/2025/TT-NHNN: the credit fund's (and,
    // for the calendar and the System-start record, the microfinance
    // institution's) own reporting calendar, incident and resumption, correction
    // and unlock, and System-start, upgrade and connection records. All four are
    // owned by the people's credit fund's lead.
    'UC-VN-91': 'P-VN-47',
    'UC-VN-92': 'P-VN-47',
    'UC-VN-93': 'P-VN-47',
    'UC-VN-94': 'P-VN-47',
    // 2026-10-22 — Circular 152/2026/TT-BTC persona and use-case gate
    // (docs/user-validation/circular-152-persona-usecase-gate.md). The vendor's
    // licensing lead keeps the fee and charge record per licence, conformity,
    // import or sender-name application. UC-VN-76 is held by the unregistered
    // draft in sprint-63.26.0-validation-report.md, Appendix B. Written as
    // UC-VN-77 on `main`; renumbered UC-VN-95 on merging `main` into Sprint
    // 62.53.0 (2026-10-23), because UC-VN-77 to UC-VN-94 were already taken.
    'UC-VN-95': 'P-VN-27',
    // 2026-10-24 — Circular 86/2026/TT-BTC persona and use-case gate
    // (docs/user-validation/circular-86-persona-usecase-gate.md). The customs and
    // logistics compliance lead keeps the taxpayer's customs tax administration
    // record (UC-VN-96) and runs the 1 July 2026 transition (UC-VN-97).
    'UC-VN-96': 'P-VN-21',
    'UC-VN-97': 'P-VN-21',
    // 2026-10-24 — Circulars 169/2026/TT-BCA and 170/2026/TT-BCA persona and
    // use-case gate (docs/user-validation/identity-biometrics-qcvn-16-17-persona-usecase-gate.md):
    // the Identity-Database capture supplier's scope, bringing a component into use,
    // operating evidence and readiness; the DPO links the technical evidence to the
    // processing record and impact assessment. Written as UC-VN-96 to UC-VN-100 and
    // renumbered on merging `main`, whose Circular 86 gate took UC-VN-96 and UC-VN-97.
    'UC-VN-98': 'P-VN-50',
    'UC-VN-99': 'P-VN-50',
    'UC-VN-100': 'P-VN-50',
    'UC-VN-101': 'P-VN-01',
    'UC-VN-102': 'P-VN-50',
    // Sprint 63.40.0 — domestic telecom notification, authored and walked by P-VN-04.
    'UC-VN-103': 'P-VN-04',
    // 2026-10-24 — Sprint 62.56.0, on the owner's answer to the gate's OQ-B-3: the
    // police unit's own collection evidence and the focal unit's system evidence.
    // Written as UC-VN-103 and UC-VN-104; renumbered on the merge with `main`, whose
    // Sprint 63.40.0 took UC-VN-103.
    'UC-VN-104': 'P-VN-51',
    'UC-VN-105': 'P-VN-51',
    // 2026-10-26 — P-VN-52 Finance & Tax Accounting Lead, the owner's decision
    // OQ-86-1 (circular-86-persona-usecase-gate.md §7, circular-152-persona-usecase-gate.md §11):
    // a customs tax payment and its allocation, a refund dossier from figures to
    // decision, a tax-debt instrument, and the fee lines of a licence application.
    'UC-VN-106': 'P-VN-52',
    'UC-VN-107': 'P-VN-52',
    'UC-VN-108': 'P-VN-52',
    'UC-VN-109': 'P-VN-52',
    // 2026-10-31 — the vehicle-monitoring draft QCVN persona and use-case gate
    // (docs/user-validation/vehicle-monitoring-qcvn-draft-persona-usecase-gate.md):
    // a draft-only overlay on Decree 319. The fleet operator keeps the device
    // inventory, the vendor evidence request and the briefing and final-text
    // recheck; the AI governance lead reviews model updates apart from the
    // classification; the DPO runs the deployment privacy review. No persona added.
    'UC-VN-110': 'P-VN-29',
    'UC-VN-111': 'P-VN-29',
    'UC-VN-112': 'P-VN-07',
    'UC-VN-113': 'P-VN-01',
    'UC-VN-114': 'P-VN-29',
    // 2026-10-31 — the persona and use-case gate for the MST consultation draft
    // Circular on digital platforms (docs/user-validation/digital-platforms-circular-draft-persona-usecase-gate.md):
    // a platform's scope and deployment-role determination, its minimum-requirement
    // conformity file, and its label, DPID notification and withdrawal response.
    // Draft-instrument readiness. P-VN-06 is the fallback for owner question
    // OQ-DPC-G1; a dedicated platform-owner persona (proposed P-VN-53) is not registered.
    // Written as UC-VN-110 to UC-VN-112; renumbered UC-VN-115 to UC-VN-117 on
    // merging `main` (PR #3210), whose vehicle-monitoring gate took UC-VN-110 to UC-VN-114.
    'UC-VN-115': 'P-VN-06',
    'UC-VN-116': 'P-VN-06',
    'UC-VN-117': 'P-VN-06',
    // Gate P5 of Sprint 62.57.0 (2026-11-01) — the personal data processing service
    // certificate. P-VN-01 in the role of DPO of an organisation that has recorded it
    // provides a service on Decree 356 Điều 21's list; a dedicated provider-side
    // persona is owner question P5-OQ1 and is not registered.
    'UC-VN-118': 'P-VN-01',
};
/** Every authored use-case ID, sorted. */
export const ALL_USE_CASE_IDS = Object.keys(USE_CASE_PRIMARY_PERSONA);
//# sourceMappingURL=use-case-registry.js.map